import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { useBranding } from '../contexts/BrandingContext';
import { getPracticeId } from '../services/api';
import { Package, ShoppingBag, Sparkles, Clock, Check, Loader2, Star } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Shop = () => {
  const { branding } = useBranding();
  const [packages, setPackages] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('packages');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const practiceId = getPracticeId();
      
      // Fetch packages and products in parallel
      const [packagesRes, productsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/packages?practiceId=${practiceId}`),
        fetch(`${BACKEND_URL}/api/products?practiceId=${practiceId}`)
      ]);
      
      if (packagesRes.ok) {
        const packagesData = await packagesRes.json();
        setPackages(packagesData);
      }
      
      if (productsRes.ok) {
        const productsData = await productsRes.json();
        setProducts(productsData);
      }
    } catch (error) {
      console.error('Error fetching shop data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateSavings = (original, current) => {
    if (!original || original <= current) return 0;
    return Math.round((1 - current / original) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-lg">Loading shop...</span>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="shop-page">
      <Navigation />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Shop Our Favorites
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Exclusive treatment packages and premium skincare products curated by our experts
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8">
            <TabsTrigger value="packages" className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              Treatment Packages
            </TabsTrigger>
            <TabsTrigger value="products" className="flex items-center gap-2">
              <ShoppingBag className="h-4 w-4" />
              Skincare Products
            </TabsTrigger>
          </TabsList>

          {/* Packages Tab */}
          <TabsContent value="packages">
            {packages.length === 0 ? (
              <div className="text-center py-16">
                <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No packages available yet</h3>
                <p className="text-gray-600">Check back soon for exclusive treatment bundles!</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {packages.map((pkg) => {
                  const savings = calculateSavings(pkg.originalPrice, pkg.price);
                  return (
                    <Card 
                      key={pkg.id} 
                      className={`relative overflow-hidden hover:shadow-xl transition-all duration-300 ${pkg.popular ? 'ring-2 ring-primary' : ''}`}
                      data-testid={`package-${pkg.id}`}
                    >
                      {pkg.popular && (
                        <div className="absolute top-4 right-4">
                          <Badge className="bg-primary text-gray-900">
                            <Star className="h-3 w-3 mr-1" />
                            Popular
                          </Badge>
                        </div>
                      )}
                      
                      {pkg.image && (
                        <div className="aspect-video overflow-hidden">
                          <img
                            src={pkg.image}
                            alt={pkg.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div>
                            <Badge variant="outline" className="mb-2">{pkg.category}</Badge>
                            <CardTitle className="text-xl">{pkg.name}</CardTitle>
                          </div>
                        </div>
                        <CardDescription>{pkg.description}</CardDescription>
                      </CardHeader>
                      
                      <CardContent>
                        {/* Included Treatments */}
                        {pkg.includedTreatments && pkg.includedTreatments.length > 0 && (
                          <div className="mb-4">
                            <p className="text-sm font-medium text-gray-700 mb-2">Includes:</p>
                            <ul className="space-y-1">
                              {pkg.includedTreatments.map((treatment, idx) => (
                                <li key={idx} className="flex items-center text-sm text-gray-600">
                                  <Check className="h-4 w-4 text-green-500 mr-2" />
                                  {treatment}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Sessions & Validity */}
                        <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                          <div className="flex items-center">
                            <Sparkles className="h-4 w-4 mr-1" />
                            {pkg.sessions} session{pkg.sessions > 1 ? 's' : ''}
                          </div>
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 mr-1" />
                            Valid {pkg.validityDays} days
                          </div>
                        </div>
                        
                        {/* Pricing */}
                        <div className="flex items-end justify-between mb-4">
                          <div>
                            <span className="text-3xl font-bold text-gray-900">${pkg.price}</span>
                            {pkg.originalPrice > pkg.price && (
                              <span className="ml-2 text-lg text-gray-500 line-through">
                                ${pkg.originalPrice}
                              </span>
                            )}
                          </div>
                          {savings > 0 && (
                            <Badge className="bg-green-100 text-green-800">
                              Save {savings}%
                            </Badge>
                          )}
                        </div>
                        
                        <Button className="w-full" asChild>
                          <a href="#book-now">Purchase Package</a>
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            {products.length === 0 ? (
              <div className="text-center py-16">
                <ShoppingBag className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No products available yet</h3>
                <p className="text-gray-600">Check back soon for premium skincare products!</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {products.map((product) => (
                  <Card 
                    key={product.id} 
                    className="relative overflow-hidden hover:shadow-lg transition-all duration-300"
                    data-testid={`product-${product.id}`}
                  >
                    {product.featured && (
                      <div className="absolute top-3 left-3 z-10">
                        <Badge className="bg-primary text-gray-900">Featured</Badge>
                      </div>
                    )}
                    
                    <div className="aspect-square bg-gray-100 overflow-hidden">
                      {product.image ? (
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ShoppingBag className="h-16 w-16 text-gray-300" />
                        </div>
                      )}
                    </div>
                    
                    <CardContent className="p-4">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                        {product.brand}
                      </p>
                      <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2">
                        {product.name}
                      </h3>
                      {product.size && (
                        <p className="text-sm text-gray-500 mb-2">{product.size}</p>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-lg font-bold text-gray-900">${product.price}</span>
                          {product.comparePrice > product.price && (
                            <span className="ml-2 text-sm text-gray-500 line-through">
                              ${product.comparePrice}
                            </span>
                          )}
                        </div>
                        {product.inventory <= 5 && product.inventory > 0 && (
                          <Badge variant="outline" className="text-orange-600 border-orange-600">
                            Low Stock
                          </Badge>
                        )}
                      </div>
                      
                      <Button 
                        className="w-full mt-3" 
                        size="sm"
                        disabled={product.inventory === 0}
                      >
                        {product.inventory === 0 ? 'Out of Stock' : 'Add to Cart'}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* CTA Section */}
        <div className="mt-16 bg-secondary rounded-3xl p-12 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Need Help Choosing?
          </h2>
          <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
            Our team is here to help you find the perfect treatments and products for your skincare goals
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild>
              <a href="#book-now">Book a Consultation</a>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/help">Contact Us</Link>
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Shop;
